/**
 * A query-report-header component defines the saving
 * and loading mechanisms for interacting with reports
 */
import {ParamComponent} from 'panel';
import flattenDeep from 'lodash/flattenDeep';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import uniqBy from 'lodash/uniqBy';
import isString from 'lodash/isString';
import moment from 'moment';
import isEqual from 'lodash/isEqual';

import {DashboardOrigin} from 'common/report/util/bookmark-overrides';
import {userDetailsStore} from 'common/report/stores/user-details';
import {currentProjectStore} from 'common/report/stores/project-details';
import {EntityType, OverrideModal} from 'common/report/util/saved-entities/types';
import {Section, SectionItem, TooltipInfo} from 'common/components/items-menu/types';
import {getCardInDashboardHref, getDashboardAllowedReports, getLastRefreshedTimeLabel} from 'common/report/util/boards';
import {UserLocalStorageHelper} from 'common/report/user-local-storage';
import {BROWSE_URL_PARAM, getBrowseUrl, getReportNameFromSearchContext} from 'common/report/util/search';
import {SearchDisplayContext} from 'common/report/util/search/types';
import {ContentDiscoveryType, SearchSource, SearchTrackedProperty} from 'common/report/util/search/tracking';
import {
  buildCreateEntityModalAttrs,
  getEntityType,
  getEntityTypeName,
  getSharableEntityType,
  getShareCapability,
  promptSuperadminOverrideSave,
} from 'common/report/util/saved-entities';
import {dashboardUtils} from 'common/report/util/dashboards/utils';
import {parseStateFromUrl} from 'common/report/util/state-from-url';
import {emitTrackEvent, propagateEvent} from 'common/util/dom';
import {KeyModifiers} from 'common/util/keyboard';
import {serializeAttributes} from 'common/util';
import {pluralize, truncate} from 'common/util/string';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {REPORT_LOADED_COPY} from 'common/util/favicon';
import {promptSharing} from 'common/widgets/sharing';
import {
  FeatureGate,
  LocalStorageKey,
  Permission,
  ProjectFeatureFlag,
  ReportName,
  RestrictedReport,
  RestrictEntitySaving,
} from 'common/report/constants';
import {dashboardsClient} from 'common/report/queries/app-api/dashboards-client';
import {bookmarkClient} from 'common/report/queries/app-api/bookmark-client';
import {
  DISABLE_QUERY_SAMPLING,
  ENABLE_QUERY_SAMPLING,
  ENABLE_QUERY_SAMPLING_LONG,
  IconSizeType,
  QUERY_SAMPLING_DISABLED,
  QUERY_SAMPLING_ENABLED,
} from 'common/constants';
import {useStore} from 'common/util/component-helpers';
import {SharingAccessType} from 'common/widgets/sharing/types';
import {createEntityModalPromise} from 'common/widgets/create-entity-modal';
import {sharingUtils} from 'common/widgets/sharing/utils';
import {SharingModalDisabledReason, SharingModalViewType} from 'common/widgets/sharing/modal/types';
import {confirm} from 'common/components/confirm';
import {THEME} from 'common/components/confirm/types';
import {DashboardSelectorController} from 'common/report/controllers/dashboard-selector-controller';
import {IronContextRegistry} from 'common/report/context-registry';
import {DummyStorageManager} from 'common/contexts/storage-manager/dummy';
import {DummyUrlManager} from 'common/contexts/url-manager/dummy';
import {addToast} from 'common/components/toast-area/toast-manager';
import {StatusCode} from 'common/util/shared-client';
import {ToastType} from 'common/components/toast/types';
import {upsellModalPromise} from 'common/widgets/upsell-modal';
import {Placement} from 'common/components/tooltip/types';
import {ButtonTheme} from 'common/components/button/constants';
import {ContentActionType, ContentType, Dashboard} from 'common/report/dashboards/types';
import {getReportUrl, isEmbedMode} from 'common/report/util';
import {reporter} from 'common/report/error-reporting';
import {savedReportLimitInfo, savedReportsLimitStore} from 'common/report/stores/saved-reports-limit';
import {promptSaveReportLimitModal} from 'common/widgets/create-entity-modal/saved-reports-limit-utils';
import {TrackingEventProperty} from 'common/widgets/user-list/tracking';
import {generateShortcutLabel, getCommandKey} from 'common/util/keyboard';
import {HistoryManager} from 'common/contexts/history-manager';
import {customAlertApi} from 'common/widgets/alerts/custom-alert/api';
import {ActivityMonitor} from 'common/util/activity-monitor';
import {getPresenceActivityMonitor} from 'common/report/queries/app-api/presence-client';
import {PresenceResults} from 'app_api/projects/presence/types';
import {loadEntityModalPromise} from 'common/widgets/load-entity-modal';
import {GlobalAccessType, ModalReturn} from 'common/widgets/create-entity-modal/types';
import {MixpanelUserItem} from 'common/widgets/user-list/types';
import {panel2react} from 'common/util/react';
import {useReportConsistency, useSingleReport} from 'common/report/util/feature-sampling';

import {HeaderAction, QueryReportHeaderParams, SaveModes, UntitledReportName} from './types';
import {SaveTypeTrackingProperty, TrackingEvent} from './tracking';
import {template} from './template';
import alertsUpsellImage from './images/alerts-upsell-images.svg';

const RESET_ACTION = Object.freeze({
  icon: `new-report`,
  value: HeaderAction.Reset,
  label: `New`,
  elref: `reportNewButton`,
});

interface QueryReportHeaderState {
  existingEntityNames: string[];
  isBookmarkSelectorOpen: boolean;
  isDescriptionTruncated: boolean;
  saveMode: SaveModes;
  openShareUponSave: boolean;
  showSaveBeforeShare: boolean;
  dashboardsLoading: boolean;
  isCreateAlertModalOpen: boolean;
  showPublicBadge: boolean;
  // boards
  isEditingTitle: boolean;
  isEditingDescription: boolean;
  newReportName: string;
  newReportDescription: string;
  dashboardSelectorOpen: boolean;
  showBoardsSaveButtonSpinner: boolean;
  dashboardsDropdownOpen: boolean;
  canSaveToMainDashboard: boolean;
  presenceViews: MixpanelUserItem[];
  totalPresenceViews?: number;
  showSaveWarningModal: boolean;
}

type JsxProps = {
  params: QueryReportHeaderParams;
  props?: {
    showCustomAlertModal?: boolean;
    extraTrackingParams?: Record<string, string>;
  };
  on?: {
    actionclick?: (ev: CustomEvent<{value: HeaderAction}>) => void;
    bookmarkselectoropen?: (ev: CustomEvent) => void;
    reset?: (ev: CustomEvent) => void;
    save?: (
      ev: CustomEvent<{
        name: string;
        description: string;
        saveAsNew: boolean;
        dashboard: Dashboard;
        didSave?: boolean;
        event?: HeaderAction;
        globalAccessType?: GlobalAccessType;
      }>,
    ) => void;
    'back-to-board'?: (ev: CustomEvent<{clickEvent: MouseEvent}>) => void;
    bookmarkselect?: (ev: CustomEvent) => void;
    bookmarkselectorclose?: (ev: CustomEvent) => void;
  };
};
export class QueryReportHeader extends ParamComponent<
  QueryReportHeaderParams,
  QueryReportHeaderState,
  IronContextRegistry
> {
  // dashboard that contains at least one card with a link to the selected-bookmark
  public extraDashboards: DashboardOrigin[];
  // Used to check if saving/editing should trigger a warning because it affects Custom Alerts
  public customAlerts = [];
  public dashboardController: DashboardSelectorController;
  private recentlyViewedDashboardsStorage: UserLocalStorageHelper<Dashboard[]>;
  private presenceActivityMonitor: ActivityMonitor;

  private handlePublicDashboardUpdate = (evt: CustomEvent) => {
    const numActiveLinks = evt.detail[`numLinks`];
    // Once there is an update to public dashboard in sharing modal
    // we ignore the initial attribute
    // hack, TODO: need to move this one level up
    this.setParams({...this.params, isPublicDashboard: false});
    this.update({showPublicBadge: numActiveLinks > 0});
  };
  private extraTracking: {[name: string]: Obj};

  get config() {
    return {
      defaultParams: {
        autoRefreshIntervalMs: null,
        canSave: false,
        canShare: false,
        canWrite: false,
        controlItems: [],
        hideActions: [],
        invalidEntity: false,
        isSaveFailed: false,
        overflowControlItems: [],
        workspaceId: 0,
      },

      params: {
        allowStaffOverride: Boolean,

        // If set, creates an interval that fires a refresh event every x milliseconds.
        autoRefreshIntervalMs: Number,

        // Can the user change the currently selected entity?
        canSave: Boolean,

        // Can the user share the currently selected entity?
        canShare: Boolean,

        // permissions
        // Can the user create/change entities?
        canWrite: Boolean,

        computedAt: String,

        // Additional set of items to be rendered in the control bar, interface ControlItem[]
        controlItems: Array,

        customAlertTimeUnit: String,
        customAlertsDisabledText: String,

        // display context for the load entity modal: insights, flows, dashboards, etc (SearchDisplayContext)
        // enum: Object.values(SearchDisplayContext)
        displayContext: String,

        // URL containing serialized report, used to create duplicate in a new tab.
        duplicateReportUrl: String,

        emptyEntityName: String,
        enableCustomAlertConditionTypes: Boolean,
        entitiesLoading: Boolean,

        // enum: Object.values(EntityType)
        entityType: String,

        hasUnsavedChanges: Boolean,

        // Hide a set of built-in header controls.
        hideActions: Array,

        // If entity is invalid, save will be prevented - create new or edit
        invalidEntity: Boolean,

        // Optional tooltip to display above disabled "Save" button when entity is invalid
        invalidMessage: String,

        // custom-alerts
        customAlertIntervalDays: Number,
        enableAnomalyDetectionControls: Boolean,
        bookmarkParams: Object,

        isCustomAlertsActionVisible: Boolean,
        isPublicDashboard: Boolean,
        isQuerySamplingActionVisible: Boolean,
        isQuerySamplingEnabled: Boolean,
        triggerQuerySamplingTourTooltip: Boolean,

        // Save report failed
        isSaveFailed: Boolean,

        isSuperadmin: Boolean,
        isTemplateDashboard: Boolean,

        // Board where the user comes from
        originDashboard: Object,

        // Items to be rendered in the overflow section of the control bar, interface ControlItem[]
        overflowControlItems: Array,

        // as of date for projects with data in the past.
        projectAsOfDate: String,

        projectId: Number,
        querySamplingDisabledMessage: String,
        resultsKeysAndValues: Array,

        // query sampling
        samplingFactor: Number,

        // Board's main dashboard
        selectedBookmarkDashboard: Object,

        selectedEntityCreator: Object,

        selectedEntityDescription: String,

        selectedEntityEditor: Object,

        selectedEntityId: Number,
        selectedEntityName: String,
        toggleGlobalFiltersLabel: String,

        // If specified, dispatches '[mp-query-report-header] <event>' bubbled events that can be listened for tracking with tab=<track-name> as a property
        trackName: String,

        userId: Number,

        // sharing-disabled (visibility controls in create-entity-modal)
        // Message to display above visibility controls
        visibilityControlsMessage: String,

        workspaceId: Number,

        /** used to display "Saving..." or "You don't have permissions" messages, next to the title */
        boardsTitleBadgeEl: Object,
        dashboardsRoute: Array,
        // different parent components has different buttons to pass in query-report-header
        headerActionItemsEl: Object,
      },

      template,

      contexts: [
        `urlManager` as const,
        `localStorageManager` as const,
        `historyManager` as const,
        `dashboardSelectorController` as const,
        `currentProject` as const,
      ],

      defaultContexts: {
        urlManager: new DummyUrlManager(),
        localStorageManager: new DummyStorageManager(true),
        historyManager: new HistoryManager(),
      },

      defaultState: {
        existingEntityNames: [],
        isBookmarkSelectorOpen: false,
        isDescriptionTruncated: true,
        saveMode: null,
        openShareUponSave: false,
        // Represents the state where we need to show Save modal before Share modal
        showSaveBeforeShare: false,
        dashboardsLoading: false,
        isEditingTitle: false,
        isEditingDescription: false,
        isCreateAlertModalOpen: false,
        showPublicBadge: false,
        newReportName: null,
        newReportDescription: null,
        dashboardSelectorOpen: false,
        showBoardsSaveButtonSpinner: false,
        dashboardsDropdownOpen: false,
        canSaveToMainDashboard: false,
        presenceViews: [],
        showSaveWarningModal: false,
      },

      hooks: {
        preUpdate: (_state: Partial<QueryReportHeaderState>, params?: Partial<QueryReportHeaderParams>) => {
          if (!params) return;
          if (`selectedEntityName` in params) {
            this.helpers.updateDocumentTitle(params.selectedEntityName);
            this.update({newReportName: null});
          }

          if (params.selectedEntityDescription !== this.params.selectedEntityDescription) {
            this.update({newReportDescription: null});
          }

          if (
            params.selectedEntityId !== this.params.selectedEntityId ||
            !isEqual(params.originDashboard, this.params.originDashboard)
          ) {
            this.loadDashboardDataController();
          }

          const mpSharingModalElement = document.querySelector(`mp-sharing-modal`);
          if (params.isSaveFailed !== this.params.isSaveFailed && this.params.isSaveFailed && mpSharingModalElement) {
            mpSharingModalElement.removeAttribute(`open`);
          }
          // In case where a user saves a new report or saves a report as a duplicate
          if (params.selectedEntityId !== this.params.selectedEntityId || this.params.selectedEntityId === null) {
            const newEntityId = params.selectedEntityId;
            if (newEntityId && this.state.openShareUponSave) {
              // no need to disable the sharing modal when a user is the creator except for consumer as creator
              const userIsConsumer = this.isUserConsumer();
              promptSharing({
                type: this.helpers.getSharableEntityType(),
                id: newEntityId,
                'project-id': this.params.projectId,
                disabled: userIsConsumer,
                'disabled-reason': userIsConsumer ? SharingModalDisabledReason.Consumer : null,
              });
              this.update({
                openShareUponSave: false,
              });
            }

            this.resetPresenceMonitor(newEntityId, params.entityType ?? this.params.entityType);
          }

          if (this.params.hasUnsavedChanges === true && params.hasUnsavedChanges === false) {
            this.update({showBoardsSaveButtonSpinner: false});
          }
        },
      },

      helpers: {
        IconSizeType,
        serializeAttributes,
        propagateEvent: (ev) => propagateEvent(this, ev),
        userIsEntityOwner: (entity): boolean => this.params.userId === entity.userId,
        userIsStaff: (): boolean => userDetailsStore.value.isStaff,
        hasSlot: (name) => this.hasSlot(name),
        getHeaderActionOverflowTooltip: () => {
          const labels = flattenDeep(this.getHeaderActionOverflowItems().map((item) => item.items))
            .slice(0, 2)
            .map((item) => item.label);
          return `${labels.join(`, `)}, and more...`;
        },
        getShareCapability: () => {
          // query-report-header gets entity object destructured through attrs
          const entity = {
            canShare: this.params.canShare,
            canUpdateBasic: this.params.canSave,
            allowStaffOverride: this.params.allowStaffOverride,
            isSuperadmin: this.params.isSuperadmin,
          };
          return getShareCapability(entity, EntityType.Report, {
            isSharingEnabled: this.helpers.isSharingEnabled(),
          });
        },
        allowSuperAdminOverride: () =>
          /** Allow super admin override for an entity that is valid but not save-able (due to permission) and user is a superadmin */
          (!this.params.canSave && !this.params.invalidEntity && this.params.isSuperadmin) ||
          this.params.allowStaffOverride,
        handleSharingStatusButtonClick: async () => {
          // Open save modal when share is clicked in brand new report - only for share enabled projects
          if (this.helpers.shouldOpenSaveModalOnShare() && this.helpers.isSharingEnabled()) {
            this.update({
              openShareUponSave: true,
              showSaveBeforeShare: true,
            });
            this.openSaveModal(SaveModes.Save, SaveTypeTrackingProperty.Existing);
          } else {
            const sharingAccessType = this.helpers.getUserSharingAccessType();
            await promptSharing({
              type: this.helpers.getSharableEntityType(),
              id: this.params.selectedEntityId,
              'project-id': this.params.projectId,
              disabled: sharingAccessType === SharingAccessType.Viewer || sharingAccessType === SharingAccessType.None,
              'entity-name': this.params.selectedEntityName,
              'disabled-reason': sharingUtils.getSharingModalDisabledReason(sharingAccessType),
              'is-superadmin': this.helpers.allowSuperAdminOverride(),
              'entity-owner-id': this.params.selectedEntityCreator?.id,
            });
          }
        },
        handleViewPublicDashboardLinksClick: () => {
          const sharingAccessType = this.helpers.getUserSharingAccessType();
          promptSharing({
            type: this.helpers.getSharableEntityType(),
            id: this.params.selectedEntityId,
            'project-id': this.params.projectId,
            disabled: sharingAccessType === SharingAccessType.Viewer || sharingAccessType === SharingAccessType.None,
            'disabled-reason': sharingUtils.getSharingModalDisabledReason(sharingAccessType),
            'open-as': SharingModalViewType.PublicDashboardDetails,
            'entity-name': this.params.selectedEntityName,
          });
        },
        handleBoardButtonClick: (clickEvent) => {
          this.track(TrackingEvent.ReturnToBoard, {Origin: `Back Button`});
          this.dispatchEvent(new CustomEvent(`back-to-board`, {detail: {clickEvent}}));
        },
        handleBoardsSaveMenuChange: (ev: CustomEvent) => {
          if (ev.detail.state === `open`) {
            this.track(TrackingEvent.StartSave);
          }
        },
        getReturnToBoardLink: () => {
          const dashboard = this.helpers.getBoardsRedirectDashboard();
          return getCardInDashboardHref({
            dashboardId: dashboard?.id,
            bookmarkId: this.params.selectedEntityId,
            projectId: this.params.projectId,
            workspaceId: this.params.workspaceId,
          });
        },
        getCloseLink: () => {
          const urlManager = this.getContext(`urlManager`);
          if (urlManager.getQueryStringParam(BROWSE_URL_PARAM)) {
            return getBrowseUrl(urlManager, {resetFilters: false});
          }
          return this.helpers.getReturnToBoardLink();
        },
        /**
         *
         * @returns the dashboard where the report is associated. `origin-dashboard` is used when a new report is created
         */
        getBoardsMainDashboard: () => {
          return this.params.selectedBookmarkDashboard || this.getDecodedOriginDashboard();
        },
        /**
         *
         * @returns the dashboard where the user come from, and where we should redirect when the `back` arrow is clicked. If no `origin-dahsboard` is specified, we use the report's main dashboard
         */
        getBoardsRedirectDashboard: () => {
          return this.getDecodedOriginDashboard() || this.params.selectedBookmarkDashboard;
        },
        handleSaveWarningTableCellCliked: ({detail}) => {
          if (detail.context.field === `name`) {
            this.openCustomAlertPage();
          }
        },
        saveWarningModalChanged: (ev) => {
          if (ev.detail.state === `closed`) {
            this.update({showSaveWarningModal: false});
          }
        },
        saveWarningModalSubmitted: (ev) => {
          if (ev.detail.action === `confirm`) {
            this.saveReport(SaveModes.Save);
          }
        },
        boardsSaveInDashboard: async (dashboardId?: number, saveMode?: SaveModes) => {
          const {isBelowLimit, hasSavedReportLimit} = savedReportLimitInfo(this.params.projectId);
          if (hasSavedReportLimit) {
            // show how many saved reports exist no matter what
            await promptSaveReportLimitModal(this.params.projectId, saveMode === SaveModes.SaveNew);
            // reject the rest of the process if wants to save a new report over the report limit
            if (!isBelowLimit && saveMode === SaveModes.SaveNew) {
              return;
            }
          }

          const dashboards = this.dashboardController.getDashboards();
          const dashboard = dashboards.find(({id}) => id === dashboardId) || this.helpers.getBoardsMainDashboard();
          if (!isEmpty(dashboard)) {
            // verify user confirmed saving if need be
            // if current bookmark IS part of dashboards or alerts, warn user via dialog
            let confirmedSave = true;
            if (this.getNumExtraDashboards() || this.getNumCustomAlerts()) {
              if (this.getNumCustomAlerts()) {
                this.update({showSaveWarningModal: true});
                return;
              } else {
                confirmedSave = await confirm({
                  theme: THEME.CONFIRM,
                  'confirm-button-label': `Save`,
                  title: `Save Report`,
                  description: this.helpers.getEditWarning(),
                });
              }
            }
            if (confirmedSave) {
              this.saveReport(saveMode, dashboard);
            }
          }
        },
        handleAlertUpsellButtonClicked: () => {
          return upsellModalPromise(
            {
              header: `Real Time Alerts`,
              description: `Whether cart abandonment spiked or there's been a drop in user activation, Mixpanel can flag it for you. Control exactly when you're notified by selecting the reports to monitor and the thresholds that trigger alerts.`,
              'feature-gate-type': FeatureGate.AllowCustomAlerts,
              'learn-more-button-url': `/dashboards-alerts`,
              open: true,
              closeable: true,
              'upgrade-text': `Upgrade`,
            },
            {backgroundImage: alertsUpsellImage},
          );
        },
        handleCustomAlertCreated: () => {
          addToast({
            cta: `Manage Alerts`,
            ctaHref: getReportUrl(`settings#account/alert-settings`, {
              projectId: this.params.projectId,
            }),
            message: `Custom alert created.`,
            ctaTarget: `_blank`,
          });
          this.helpers.closeCustomAlertModal();
          this.loadCustomAlerts();
        },
        handleCustomAlertError: (ev) => {
          let reason = ``;
          if (ev.detail.status === StatusCode.PermissionDenied) {
            if (ev.detail.error) {
              reason = `, ${ev.detail.error}`;
            } else {
              reason = `, Unauthorized`;
            }
          }
          addToast({
            message: `Failed to create custom alert${reason}.`,
            type: ToastType.ERROR,
          });
          this.helpers.closeCustomAlertModal();
        },
        closeCustomAlertModal: () => {
          this.update({isCreateAlertModalOpen: false});
        },
        shouldOpenSaveModalOnShare: (): boolean => !this.params.selectedEntityId && !this.hasSelectedEntity,

        bookmarkSelected: (ev) => {
          this.dispatchEvent(new CustomEvent(`bookmarkselect`, {detail: ev.detail}));
        },

        // Set document title to report title
        updateDocumentTitle: (reportTitle = this.params.selectedEntityName) => {
          const projectName = this.project?.name;

          // if the title is report loaded copy, we should not override this
          if (document.title === REPORT_LOADED_COPY) {
            return;
          }

          let newTitle;
          if (reportTitle && projectName) {
            newTitle = `${reportTitle} - ${projectName} / Mixpanel`;
          } else if (reportTitle) {
            newTitle = `${reportTitle} / Mixpanel`;
          } else if (projectName) {
            newTitle = `${projectName} / Mixpanel`;
          } else {
            newTitle = `Mixpanel`;
          }

          if (newTitle !== document.title) {
            document.title = newTitle;
          }
        },

        submitSaveModal: (result: ModalReturn, saveType?: SaveTypeTrackingProperty) => {
          if (result.event === `saveAndShare`) {
            // When Share Management is clicked in Save Modal, we need to open Share modal
            this.update({
              openShareUponSave: true,
            });
          }
          //save and share has a `.value` property, otherwise the report name and description is on the object
          this.submitSave({newReportData: result.value ?? result, saveType});
        },

        /** Show save modal when editing is enabled */
        editReportClicked: async (ev: Event) => {
          // prevent the event from bubbling, without it a click on the edit button (pencil) would bubble
          // up to the title and cause a double modal invocation.
          ev.stopPropagation();
          if (this.helpers.allowSuperAdminOverride()) {
            const modalOutcome = await promptSuperadminOverrideSave(
              getEntityType(this.helpers.getSharableEntityType()),
            );
            if (modalOutcome === OverrideModal.Copy) {
              this.openSaveModal(SaveModes.SaveNew);
            } else if (modalOutcome !== OverrideModal.Closed) {
              this.openSaveModal(this.hasSelectedEntity ? SaveModes.Edit : SaveModes.Save);
            }
          } else {
            this.openSaveModal(this.hasSelectedEntity ? SaveModes.Edit : SaveModes.Save);
          }
        },

        getDashboardSelectorExcludedDashboards: () => {
          const excludedDashboards = this.extraDashboards.map((dash) => dash.id);
          excludedDashboards.push(this.helpers.getBoardsMainDashboard()?.id);
          return excludedDashboards;
        },

        getSamplingNotice: ({isReportModeEnabled = true} = {}) => {
          const samplingFactor = this.params.samplingFactor;
          if (samplingFactor && samplingFactor < 1) {
            if (isReportModeEnabled) {
              return `Using ${samplingFactor * 100}% of data. Results are approximate.
              [Learn More](https://docs.mixpanel.com/docs/analysis/advanced/other-advanced-features#query-time-sampling)`;
            } else {
              return `Sampled at ${samplingFactor * 100}%`;
            }
          }

          return null;
        },

        onOverflowMenuClick: () => {
          this.track(TrackingEvent.OverflowMenuClicked, {'Display Context': this.params.displayContext});
          this.update();
        },

        onHeaderActionClick: async (ev) => {
          switch (ev.detail.value) {
            case HeaderAction.Edit:
              if (this.isReportModeEnabled()) {
                this.update({isEditingTitle: true});
              } else {
                this.openSaveModal(SaveModes.Edit, SaveTypeTrackingProperty.Renamed);
              }
              this.track(TrackingEvent.StartReportRename, {'Rename Location': `Overflow`});
              break;
            case HeaderAction.Load:
              this.openBookmarkSelector();
              break;
            case HeaderAction.SaveNew:
              if (!this.hasSelectedEntity) {
                // if no bookmark is currently selected, show bookmark edit modal
                this.openSaveModal(SaveModes.Save);
              } else {
                this.openSaveModal(SaveModes.SaveNew);
              }
              break;
            case HeaderAction.Save:
              if (!this.hasSelectedEntity) {
                // if no bookmark is currently selected, show bookmark edit modal
                this.openSaveModal(SaveModes.Save);
              } else if (this.getNumExtraDashboards() || this.getNumCustomAlerts()) {
                // if current bookmark IS part of dashboards or alerts, warn user via dialog
                confirm({
                  theme: THEME.CONFIRM,
                  'confirm-button-label': `Save`,
                  title: `Save Report`,
                  description: this.helpers.getEditWarning(),
                }).then((confirmed) => {
                  if (confirmed && this.hasUnsavedChanges) {
                    this.submitSave({});
                  }
                });
              } else if (this.helpers.allowSuperAdminOverride()) {
                const modalOutcome = await promptSuperadminOverrideSave(
                  getEntityType(this.helpers.getSharableEntityType()),
                );
                if (modalOutcome === OverrideModal.Copy) {
                  this.openSaveModal(SaveModes.SaveNew);
                } else if (modalOutcome !== OverrideModal.Closed && this.hasUnsavedChanges) {
                  this.submitSave({}); // save directly
                }
              } else if (this.hasUnsavedChanges) {
                this.submitSave({}); // save directly without any warning dialog
              }
              break;
            case HeaderAction.Reset:
              this.handleResetAction();
              break;
            case HeaderAction.CreateAlert:
              this.update({isCreateAlertModalOpen: true});
              break;
            case HeaderAction.ManageAlert:
              this.update({isCreateAlertModalOpen: false});
              this.openCustomAlertPage();
              break;
            case HeaderAction.EnableAlerts:
              this.helpers.handleAlertUpsellButtonClicked();
              break;
            case HeaderAction.QuerySampling:
              this.helpers.handleToggleQuerySampling(ev);
              break;
            case HeaderAction.Delete:
              this.track(TrackingEvent.StartReportDelete);
              this.dispatchEvent(new CustomEvent(`actionclick`, {detail: ev.detail}));
              break;
            case HeaderAction.Undo:
              this.getContext(`historyManager`).undo();
              break;
            case HeaderAction.Redo:
              this.getContext(`historyManager`).redo();
              break;
            default:
              this.dispatchEvent(new CustomEvent(`actionclick`, {detail: ev.detail}));
              break;
          }
          // when ev.detail.name === move, the ev.detail.value is a number and we instead want to use the ev.detail.name as the selected action for tracking
          this.track(TrackingEvent.OverflowMenuActionSelected, {
            'Selected Action': ev.detail.name === ContentActionType.Move ? ev.detail.name : ev.detail.value,
            'Display Context': this.params.displayContext,
          });
        },

        getEditWarning: () => {
          const numExtraDashboards = this.getNumExtraDashboards();
          const numAlerts = this.getNumCustomAlerts();

          if (!numExtraDashboards && !numAlerts) {
            return ``;
          }

          // another Dashboard or 2 other Dashboards
          const numDashboardsText = numExtraDashboards
            ? `${numExtraDashboards === 1 ? `another` : `${numExtraDashboards} other`} ${pluralize(
                `Board`,
                numExtraDashboards,
              )}`
            : ``;
          // an Alert or 2 Alerts
          const numAlertsText = numAlerts
            ? `${numAlerts === 1 ? `an` : numAlerts} ${pluralize(`Alert`, numAlerts)}`
            : ``;
          const conjunction = numAlerts && numExtraDashboards ? ` and ` : ``;

          const alertText = numAlerts > 1 ? `alerts` : `alert`;
          const pointerText = numAlerts > 1 ? `these` : `this`;
          if (numAlertsText) {
            const boardActiveText = numDashboardsText ? ` and is active on ` + numDashboardsText : ``;
            return `This report is currently used to query ${pointerText} ${alertText}${boardActiveText}. Saving changes to the report may affect the ${alertText} output or cause the ${alertText} to stop running. If you wish to avoid invalidating ${pointerText} ${alertText}, please save as new report.`;
          }
          return [
            `This report is currently active on`,
            ` ${numAlertsText}${conjunction}${numDashboardsText}.`,
            ` By continuing, you’ll be updating the data across all instances.`,
          ].join(``);
        },

        buildCreateEntityModalAttrs: () => {
          const isDuplicating = this.state.saveMode === SaveModes.SaveNew;
          const isEditing = this.hasSelectedEntity && !isDuplicating;
          const editWarningMessage = this.helpers.getEditWarning();
          const showEditWarning = !!editWarningMessage;

          return buildCreateEntityModalAttrs({
            modalTitleOverride: this.state.showSaveBeforeShare ? `Save Before Sharing` : null,
            modalOpen: true,
            modalCloseable: this.params.entityType === EntityType.Dashboard,
            isEditing,
            isDuplicating,
            projectId: this.params.projectId,
            entityType: this.params.entityType,
            // showSaveBeforeShare represents if a Save modal was triggered by the Share button.
            // We show Share Management button in the Save modal only when Save modal was triggered by Save button
            showShareManagement: this.helpers.isSharingEnabled() && !this.state.showSaveBeforeShare,
            entity: {
              name: this.params.selectedEntityName,
              description: this.params.selectedEntityDescription,

              // permissions
              can_view: this.params.isSuperadmin || this.params.canSave,
              can_update_basic: this.params.canSave,
              can_share: this.params.canShare,
              is_superadmin: this.params.isSuperadmin,
              allow_staff_override: this.params.allowStaffOverride,
            },
            editWarning: showEditWarning && editWarningMessage,
            // Show copy link button on Save Before Share stage
            showCopyLinkButton: this.state.showSaveBeforeShare,
          });
        },

        canUserShare: () => this.helpers.getShareCapability().enableShare,
        getUserSharingAccessType: () => this.helpers.getShareCapability().sharingAccessType,
        isShareButtonEnabled: () => {
          const selectedBookmarkId = this.params.selectedEntityId;
          // When a user loads a report and makes changes, the URL loses bookmark-id
          // in case a user either refreshes the page OR share the modified URL
          // we lose bookmark-id but app thinks we have selected a report
          // in this case we should disable share button as all share APIs will fail w/o bookmark-id
          if (this.hasSelectedEntity && !selectedBookmarkId) {
            return false;
          } else if (this.params.invalidEntity) {
            return false;
          } else if (!this.hasSelectedEntity && !selectedBookmarkId && !this.params.invalidEntity) {
            // Share button should be enabled for unsaved reports
            return true;
          } else if (!this.helpers.canUserShare()) {
            // Viewer/Consumer is not allowed to share, but should be able to access Viewer version of Share Modal
            return this.helpers.getUserSharingAccessType() !== SharingAccessType.Editor;
          } else {
            return true;
          }
        },

        getShareButtonTheme: () => {
          if (this.isReportModeEnabled() || this.isDashboardModeEnabled()) {
            return ButtonTheme.Tertiary;
          }
          return ButtonTheme.Primary;
        },

        showShareButton: () => {
          const sharableEntityType = this.helpers.getSharableEntityType();
          if (!sharableEntityType) {
            return false;
          }
          const {appName} = parseStateFromUrl(window.location.pathname) || {};
          const isUsersPage = this.params.entityType === EntityType.Report && appName === ReportName.Users;
          if (isUsersPage) {
            // TODO this logic is duplicated in mp-context, figure out how to consolidate
            return (
              (currentProjectStore.value?.hasAccessToReport(RestrictedReport.Cohorts) ||
                currentProjectStore.value?.hasFeatureFlag(ProjectFeatureFlag.Cohorts)) &&
              currentProjectStore.value?.canSaveEntity(RestrictEntitySaving.Cohorts)
            );
          }
          return true;
        },

        isSharingEnabled: () => {
          return currentProjectStore.value?.getFeatureGate(FeatureGate.Sharing);
        },

        getSharableEntityType: () => this.params.entityType && getSharableEntityType(this.params.entityType),
        getTitle: () => {
          if (this.isDashboardModeEnabled()) {
            // length of the empty space for the title
            let truncatedLength = 120;
            // make space for the view only copy on the right
            if (!this.canSave() && this.hasUnsavedChanges) {
              truncatedLength = 99;
            }
            return (
              truncate(this.state.newReportName || this.params.selectedEntityName, truncatedLength) ||
              this.params.emptyEntityName
            );
          }
          return this.state.newReportName || this.params.selectedEntityName || this.params.emptyEntityName;
        },

        getShareTooltipText: () => {
          const shareActionItem = this.getShareButtonAction();
          return shareActionItem.tooltip?.text ?? ``;
        },

        handleAutoRefresh: () =>
          this.dispatchEvent(new CustomEvent(`actionclick`, {detail: {value: HeaderAction.Refresh}})),
        handleToggleQuerySampling: (ev) => {
          this.dispatchEvent(
            new CustomEvent(`actionclick`, {detail: {...ev.detail, value: HeaderAction.QuerySampling}}),
          );
          addToast({
            message: this.params.isQuerySamplingEnabled ? QUERY_SAMPLING_DISABLED : QUERY_SAMPLING_ENABLED,
            type: ToastType.SUCCESS,
          });
        },
        handleDismissQuerySamplingTour: (ev) => {
          this.dispatchEvent(
            new CustomEvent(`actionclick`, {detail: {...ev.detail, value: HeaderAction.DismissQuerySamplingTour}}),
          );
        },
        isReportModeEnabled: () => this.isReportModeEnabled(),
        isDashboardModeEnabled: () => this.isDashboardModeEnabled(),
        isHeaderReady: () => !!currentProjectStore?.value,
        handleEditReportName: () => {
          this.track(TrackingEvent.StartReportRename, {'Rename Location': `Title`});
          this.update({isEditingTitle: true});
        },
        handleEditReportDescription: () => {
          this.track(TrackingEvent.StartReportEditDescription);
          this.update({isEditingDescription: true});
        },
        isAlertFeatureGateEnabled: () => {
          return currentProjectStore.value?.getFeatureGate(FeatureGate.AllowCustomAlerts);
        },
        isReportSaved: () => Boolean(this.params.selectedEntityId) && !this.hasUnsavedChanges,
        isAlertButtonDisabled: () => {
          return !this.helpers.isReportSaved() || Boolean(this.params.customAlertsDisabledText);
        },
        isEditingReportNameOrDescription: () => this.state.isEditingDescription || this.state.isEditingTitle,
        getAlertsTooltipText: () => {
          if (!this.helpers.isReportSaved()) {
            return `To enable alerts, save your report`;
          }

          return this.params.customAlertsDisabledText || `Enable Alerts`;
        },
        getSaveAction: () => {
          const saveTooltipText = this.getSaveTooltipText();
          return {
            icon: `save`,
            label: `Save`,
            tooltip: saveTooltipText ? {text: saveTooltipText} : null,
            value: HeaderAction.Save,
            elref: `reportSaveButton`,
            isDisabled: this.isSaveButtonDisabled(),
          };
        },
        getReportName: () => {
          return this.state.newReportName === null
            ? this.params.selectedEntityName || UntitledReportName
            : this.state.newReportName;
        },
        getReportDescription: () => {
          return isString(this.state.newReportDescription)
            ? this.state.newReportDescription
            : this.params.selectedEntityDescription;
        },
        handleLinkDashboard: (ev) => {
          this.onAddReportToDashboard(ev.detail.id);
          this.update({dashboardSelectorOpen: false});
        },
        handleAddToAnotherDashboard: () => {
          this.update({dashboardSelectorOpen: true});
        },
        showViewOnlyTextHighlight: () =>
          this.hasSelectedEntity &&
          this.hasUnsavedChanges &&
          this.isReportModeEnabled() &&
          !this.canEditExistingEntity(),
        isFreePlan: (): boolean => {
          return currentProjectStore.value?.eventsPlanName === `Free`;
        },
      },
    };
  }

  public connectedCallback() {
    super.connectedCallback();

    document.addEventListener(`PublicDashboardUpdated`, this.handlePublicDashboardUpdate);
    // update header when user details are updated (from backend or otherwise)
    const userStoreCallback = () => this.helpers.updateDocumentTitle();
    useStore(this, userDetailsStore, userStoreCallback).fetch();

    const projectStoreCallback = () => {
      if (!this.presenceActivityMonitor) {
        this.resetPresenceMonitor(this.params.selectedEntityId, this.params.entityType);
      }
      this.update();
    };
    useStore(this, currentProjectStore, projectStoreCallback).fetch();

    if (this.isReportModeEnabled()) {
      useStore(this, savedReportsLimitStore, () => this.update()).fetch({projectId: this.params.projectId});
    }

    this.recentlyViewedDashboardsStorage = new UserLocalStorageHelper({
      namespace: LocalStorageKey.RecentlyViewedDashboards,
      context: this.getContext(`localStorageManager`),
    });

    this.dashboardController = this.getContext(`dashboardSelectorController`);
    this.loadDashboardDataController();

    this.loadCustomAlerts();

    this.setSuperProperties({'Has Boards UI Test': true});
  }

  public disconnectedCallback() {
    // Reset document title as page navigates away
    this.helpers.updateDocumentTitle(``);
    document.removeEventListener(`PublicDashboardUpdated`, this.handlePublicDashboardUpdate);
    this.presenceActivityMonitor?.stop();
    super.disconnectedCallback();
  }

  saveReport(saveMode, dashboard = null) {
    this.update({saveMode, showBoardsSaveButtonSpinner: true});
    this.submitSave({
      newReportData: {
        name: this.helpers.getReportName(),
        description: this.helpers.getReportDescription(),
      },
      dashboard: dashboard || this.helpers.getBoardsMainDashboard(),
    });
  }

  get hasSelectedEntity() {
    return Boolean(this.params.selectedEntityId);
  }

  get hasUnsavedChanges() {
    // if there is no entity selected/saved, then by definition there are unsaved changes
    return (
      !this.hasSelectedEntity ||
      this.params.hasUnsavedChanges ||
      (this.isReportModeEnabled() &&
        (this.params.selectedEntityName !== this.helpers.getReportName() ||
          this.params.selectedEntityDescription !== this.helpers.getReportDescription()))
    );
  }

  get project() {
    return userDetailsStore.value.projects[this.params.projectId] || null;
  }

  set existingEntityNames(existingEntityNames) {
    this.update({existingEntityNames});
  }

  set extraTrackingParams(val: {[name: string]: Obj}) {
    this.extraTracking = val;
    this.update();
  }

  get extraTrackingParams(): {[name: string]: Obj} {
    return this.extraTracking;
  }

  private _triedToShowCustomAlertModal = false;
  set showCustomAlertModal(val: boolean) {
    // only try one time
    if (val && !this._triedToShowCustomAlertModal) {
      this._triedToShowCustomAlertModal = true;
      this.tryToShowCreateAlertDialog();
    }
  }

  private getNumExtraDashboards() {
    return this.extraDashboards?.length;
  }

  private getNumCustomAlerts() {
    return this.customAlerts.length;
  }
  private async loadCustomAlerts() {
    if (this.hasSelectedEntity && this.params.isCustomAlertsActionVisible) {
      try {
        const customAlerts = await customAlertApi(this.params.projectId).loadCustomAlertsForBookmark(
          this.params.selectedEntityId,
        );
        this.customAlerts = customAlerts || [];
      } catch (error) {
        reporter.error(error as Error, `Failed to get Custom Alerts`);
      }
    }
  }

  /**
   * Checks for presence of a slotted child element
   */
  private hasSlot(slotName: string): boolean {
    return !!this.querySelector(`[slot="${slotName}"]`);
  }

  private openBookmarkSelector() {
    this.dispatchEvent(new CustomEvent(`bookmarkselectoropen`));

    loadEntityModalPromise({
      'entities-loading': this.params.entitiesLoading,
      'entity-type-filter': this.params.displayContext,
      'project-id': this.params.projectId.toString(),
      'user-id': this.params.userId,
      'workspace-id': this.params.workspaceId,
      'filter-default-open': true,
    }).then((result) => {
      if (result.type === `close`) {
        this.dispatchEvent(new CustomEvent(`bookmarkselectorclose`));
      } else if (result.type === `select`) {
        this.helpers.bookmarkSelected(result);
      }
    });

    this.track(TrackingEvent.ReportListOpen, {
      [SearchTrackedProperty.ContentDiscoveryType]: ContentDiscoveryType.Browse,
      [SearchTrackedProperty.Source]: SearchSource.Modal,
    });
  }

  handleResetAction() {
    if (!this.helpers.isReportModeEnabled()) {
      this.dispatchEvent(new CustomEvent(`reset`));
    }
    // creates a new report in a new tab but don't reset the current report (for board reports only)
  }

  private openSaveModal(saveMode: SaveModes, saveType?: SaveTypeTrackingProperty) {
    this.update({
      saveMode,
    });
    createEntityModalPromise(this.helpers.buildCreateEntityModalAttrs(), {
      existingEntityNames: this.state.existingEntityNames,
    }).then((result) => {
      if (result.didSave) {
        this.helpers.submitSaveModal(result, saveType);
      } else {
        this.closeSaveModal(result);
      }
    });
  }

  private closeSaveModal(result) {
    // When Cancel button is clicked inside the Save modal triggered by the Share button
    // we need to clear openShareUponSave state in order to prevent the Share modal
    // showing up in subsequent Save Modal unexpectedly
    const clearOpenShareUponSave = result.clearOpenShareUponSave;
    this.update({
      saveMode: null,
      showSaveBeforeShare: false,
      openShareUponSave: clearOpenShareUponSave ? false : this.state.openShareUponSave,
    });
  }

  autoSaveNameAndDescription() {
    if (this.helpers.isDashboardModeEnabled() && this.hasSelectedEntity) {
      this.dispatchEvent(
        new CustomEvent(`save`, {
          detail: {
            description: this.helpers.getReportDescription(),
            didSave: true,
            event: `save`,
            saveAsNew: false,
          },
        }),
      );
    } else if (this.hasSelectedEntity) {
      this.helpers.boardsSaveInDashboard(this.helpers.getBoardsMainDashboard()?.id, SaveModes.Save);
    }
  }

  handleReportTitleChanged(value: string) {
    const prevReportName = this.helpers.getReportName();
    if (!value.trim()) {
      // empty name should default to Untitled
      this.update({isEditingTitle: false, newReportName: UntitledReportName});
    } else {
      this.update({isEditingTitle: false, newReportName: value});
    }
    if (!this.hasSelectedEntity) {
      this.dispatchEvent(
        new CustomEvent(`editreport`, {
          detail: {name: this.state.newReportName},
        }),
      );
    }
    if (prevReportName !== this.state.newReportName) {
      this.autoSaveNameAndDescription();
    }
  }

  submitSave({
    newReportData = null,
    saveType,
    dashboard,
  }: {
    newReportData?: {
      didSave?: boolean;
      event?: HeaderAction;
      name?: string;
      description?: string;
      globalAccessType?: GlobalAccessType;
    };
    saveType?;
    dashboard?;
  }) {
    const saveAsNew = this.state.saveMode === SaveModes.SaveNew || !this.hasSelectedEntity;
    if (!saveType) {
      saveType = saveAsNew ? SaveTypeTrackingProperty.New : SaveTypeTrackingProperty.Existing;
    }

    const reportData = {
      name: this.params.selectedEntityName,
      description: this.params.selectedEntityDescription,
      saveAsNew,
      dashboard: dashboard || this.params.selectedBookmarkDashboard,
      ...(newReportData || {}),
    };

    this.dispatchEvent(new CustomEvent(`save`, {detail: reportData}));

    this.update({
      isEditingTitle: false,
      // we update newReportData.name to update the report title before the save API returns
      ...(newReportData?.name ? {newReportName: newReportData.name} : {}),
    });
    const mainDashboard = this.helpers.getBoardsMainDashboard();
    const redirectDashboard = this.helpers.getBoardsRedirectDashboard();
    this.track(TrackingEvent.ReportSaved, {
      'is owner': saveAsNew || this.isOwnerUser(),
      name: this.params.selectedEntityName,
      description: this.params.selectedEntityDescription,
      'Save Type': saveType,
      ...(mainDashboard ? {'dashboard id': mainDashboard.id} : {}),
      ...(redirectDashboard ? {'redirect dashboard id': redirectDashboard.id} : {}),
      'card id': `report-${this.params.selectedEntityId}`,
    });

    // This opens a temporary Share Modal until save API finishes and returns. Once actual save completes via API,
    // another Share Modal will trigger (in attributeChangedCallback) w/the correct bookmark-id
    if (this.state.openShareUponSave) {
      promptSharing({
        type: this.helpers.getSharableEntityType(),
        // if a bookmark is already selected, we are editing an existing report.
        id: this.params.selectedEntityId,
        'project-id': this.params.projectId,
        disabled: this.hasSelectedEntity ? false : true,
      });
    }
  }

  getHeaderActionItemsSlots() {
    const controlItems = this.getControlItems();
    return controlItems.filter((item) => Object.prototype.hasOwnProperty.call(item, `slot`));
  }

  private getControlItems(): SectionItem[] {
    let controlItems = this.params.controlItems;
    if (this.isReportModeEnabled()) {
      controlItems = [...controlItems, {slot: `boardsDuplicate`}];
    }
    return controlItems as SectionItem[];
  }

  isOwnerUser() {
    return this.params.userId === this.params.selectedEntityCreator?.id;
  }

  getHeaderActionOverflowItems(): Section[] {
    const editItem = {
      icon: `edit`,
      value: HeaderAction.Edit,
      label: `Rename`,
      elref: `reportOverflowEditButton`,
    };
    // TODO right now the deletion logic is on each report, when the users report combines reports and cohorts without
    // having separate logic to delete separately we can move bookmark deletion logic back to this component
    // rather than separating it all out
    const deleteItem = {
      icon: `trashcan`,
      value: HeaderAction.Delete,
      label: `Delete`,
      elref: `reportDeleteButton`,
    };

    const REFRESH_ACTION = Object.freeze({
      icon: `refresh`,
      value: HeaderAction.Refresh,
      label: `Refresh Data`,
      ...(this.isReportModeEnabled() && this.params.computedAt
        ? {subLabel: getLastRefreshedTimeLabel(moment(this.params.computedAt))}
        : []),
      elref: `reportRefreshButton`,
    });

    const duplicateItem = {
      label: `Duplicate`,
      value: HeaderAction.Duplicate,
      icon: `duplicate`,
    };

    const overflowItems = this.params.overflowControlItems;
    if (this.isReportModeEnabled()) {
      return [
        {
          items: [
            ...(this.canDelete() ? [deleteItem] : []),
            ...(this.canEditExistingEntity() ? [editItem] : []),
            ...this.getUndoRedoActions(),
          ],
        },
        {
          items: [
            {
              ...RESET_ACTION,
              href: getReportUrl(this.params.displayContext, {
                projectId: this.params.projectId,
                workspaceId: this.params.workspaceId,
              }),
              target: `_blank`,
              label: `New Report`,
            },
            ...(this.params.isCustomAlertsActionVisible ? this.getAlertItems() : []),
            ...(overflowItems?.length >= 1 ? overflowItems[0].items : []), // export
          ],
        },
        {
          items: [REFRESH_ACTION],
        },
      ];
    } else if (this.isDashboardModeEnabled()) {
      return [
        {
          items: this.filterHiddenActions([
            ...(this.canDelete() ? [deleteItem] : []),
            ...(this.canEditExistingEntity() ? [editItem] : []),
            duplicateItem,
          ]),
        },
        {
          items: this.filterHiddenActions(this.getUndoRedoActions()),
        },
        ...overflowItems,
        {
          items: [REFRESH_ACTION],
        },
      ];
    } else {
      return [
        {
          items: this.filterHiddenActions([
            ...(this.canDelete() ? [deleteItem] : []),
            ...(this.canEditExistingEntity() ? [editItem] : []),
          ]),
        },
        ...(this.params.isQuerySamplingActionVisible ? [{items: this.getQuerySamplingOverflowItems()}] : []),
        {
          items: [REFRESH_ACTION],
        },
      ];
    }
  }

  async onAddReportToDashboard(targetDashboardId) {
    const targetDashboard = this.dashboardController.getDashboards().find((d) => d.id === targetDashboardId);

    try {
      await dashboardsClient.addNewContentToDashboard({
        contentDetails: {
          contentParams: {source_bookmark_id: this.params.selectedEntityId},
          contentType: ContentType.ReportLink,
        },
        dashboardId: targetDashboardId,
        projectId: this.params.projectId,
        workspaceId: this.params.workspaceId,
      });

      // only add the dashboard just linked to displayed dashboards if it doesn't exist there yet
      if (
        !this.getAllDashboards()
          .map((d) => d.id)
          .includes(targetDashboard.id)
      ) {
        // adding this dashboard directly to extraDashboards is a shortcut so that we don't
        // have to requery linked-dashboard-ids/ all over again
        if (this.extraDashboards?.length > 1) {
          this.extraDashboards.push(targetDashboard);
        } else {
          this.extraDashboards = [targetDashboard];
        }
        // update sections and title
        this.update();
      }
      this.track(TrackingEvent.LinkOnDashboard);
      addToast({
        message: `Card successfully saved on ${targetDashboard.title}`,
        cta: `Go to board`,
        ctaCallback: () =>
          window.location.assign(
            getCardInDashboardHref({
              dashboardId: targetDashboard.id,
              bookmarkId: this.params.selectedEntityId,
              projectId: this.params.projectId,
              workspaceId: this.params.workspaceId,
            }),
          ),
        type: ToastType.SUCCESS,
      });
    } catch (error) {
      reporter.error(error as Error, `failure to add bookmark to dashboard`);
      addToast({
        message: `Error adding card to ${targetDashboard.title}`,
        type: ToastType.ERROR,
      });
    }
  }

  getRecentlyViewedDashboards() {
    const dashboards =
      this.recentlyViewedDashboardsStorage?.getItem({userId: this.params.userId, key: this.params.projectId}) || [];
    return dashboards.map((dashboard) => dashboard.id);
  }

  private isUserConsumer(): boolean {
    const userIsConsumer = !userDetailsStore.value.projects[this.params.projectId]?.permissions.includes(
      Permission.EditCreatedEntityVisibility,
    );
    return userIsConsumer && !userDetailsStore.value.isStaff;
  }

  canSaveNewReport() {
    return this.params.canWrite && !this.params.invalidEntity;
  }

  isSaveButtonDisabled() {
    return (
      useSingleReport() ||
      useReportConsistency() ||
      !this.canSave() ||
      !this.hasUnsavedChanges ||
      !this.params.canWrite ||
      this.state.dashboardsLoading ||
      (this.helpers.getBoardsMainDashboard() && !this.state.canSaveToMainDashboard)
    );
  }

  openCustomAlertPage() {
    window.open(`/settings/account/alert-settings/report/${this.params.selectedEntityId}`);
  }

  isDuplicateButtonDisabled() {
    return this.params.invalidEntity || !this.params.canWrite;
  }

  canSave() {
    return !this.params.invalidEntity && (this.params.canSave || this.helpers.allowSuperAdminOverride());
  }

  canEditExistingEntity() {
    // this is different from canDelete bc we should be able to delete even if we can't write, as long as we can save
    return (
      this.hasSelectedEntity &&
      ((this.params.canWrite && this.params.canSave) || this.helpers.allowSuperAdminOverride())
    );
  }

  canDelete() {
    return this.hasSelectedEntity && (this.params.canSave || this.helpers.allowSuperAdminOverride());
  }

  private async loadDashboardDataController() {
    this.update({showBoardsSaveButtonSpinner: false});
    if (
      isNil(this.extraDashboards) &&
      this.dashboardController &&
      this.isReportModeEnabled() &&
      (this.params.selectedEntityId || this.params.originDashboard)
    ) {
      this.update({dashboardsLoading: true});

      const loadDashboardsPromise = this.dashboardController.loadDashboards().then(() => {
        this.update({dashboardsLoading: false});

        // this section is still necessary for now in the case where we are creating a new report on a dashboard but we haven't
        // populated the dashboard permissions, so we should still be preventing users who don't have the ability to edit the
        // main dashboard from being able to edit
        // TODO @tiffany remove this section after we ensure can-write is accurate or this is rewritten
        const mainDashboard = this.helpers.getBoardsMainDashboard();
        if (mainDashboard) {
          const dashboard = this.dashboardController.getDashboards().find((d) => d.id === mainDashboard.id);
          if (!dashboard) {
            //fix for when dashboardController just loads the dashboards from cache while waiting for network response and main dashboard isn't in cache repsonse yet
            this.update({
              canSaveToMainDashboard: dashboardUtils.canEditDashboard({
                allow_staff_override: mainDashboard.allow_staff_override,
                is_superadmin: mainDashboard.is_superadmin,
                can_update_basic: mainDashboard.can_update_basic,
              }),
            });
          } else {
            this.update({
              canSaveToMainDashboard: dashboardUtils.canEditDashboard(dashboard),
            });
          }
        }
      });

      const bookmarkId = this.params.selectedEntityId;
      if (bookmarkId) {
        const loadLinkedIdsPromise = bookmarkClient.getLinkedDashboardIds({
          scope: {
            projectId: this.params.projectId,
            workspaceId: this.params.workspaceId,
          },
          params: {
            id: bookmarkId,
          },
        });

        const [, extraDashboardIds] = await Promise.all([loadDashboardsPromise, loadLinkedIdsPromise]);
        // TODO @gonzalo or @tiffany: extraDashboards should be in the state since we want to update the UI every time it is updated
        this.extraDashboards = extraDashboardIds.map((id) =>
          this.dashboardController.getDashboards().find((d) => d.id === id),
        );
        // update sections and title
        this.update();
      } else {
        await loadDashboardsPromise;
      }
    }
  }

  getBoardsDashboardsSections(): Section[] {
    const mainDashboard = this.helpers.getBoardsMainDashboard();
    const bookmarkId = this.params.selectedEntityId;
    const projectId = this.params.projectId;
    const workspaceId = this.params.workspaceId;

    if (!mainDashboard) {
      return [];
    }
    return [
      {
        label: `Original Board`,
        items: [
          {
            label: mainDashboard.title,
            value: mainDashboard.title,
            isSelected: false,
            subLabel: mainDashboard.creator_name,
            href: bookmarkId
              ? getCardInDashboardHref({
                  dashboardId: mainDashboard.id,
                  bookmarkId,
                  projectId,
                  workspaceId,
                })
              : getReportUrl(`dashboards#id=${mainDashboard?.id}`, {projectId, workspaceId}),
          },
        ],
      },
      {
        items:
          this.extraDashboards
            ?.filter((d) => mainDashboard && d.id !== mainDashboard.id)
            .map((dashboard) => ({
              label: dashboard.title,
              value: dashboard.title,
              subLabel: dashboard.creator_name,
              isSelected: false,
              href: bookmarkId
                ? getCardInDashboardHref({
                    dashboardId: dashboard.id,
                    bookmarkId,
                    projectId,
                    workspaceId,
                  })
                : getReportUrl(`dashboards#id=${dashboard?.id}`, {projectId, workspaceId}),
            })) || [],
      },
    ];
  }

  /**
   * @returns list of all the dashboards where this report is present, including its master and linked dashboards
   */
  getAllDashboards(): DashboardOrigin[] {
    let dashboards = [];
    if (this.params.selectedBookmarkDashboard) {
      dashboards = [this.params.selectedBookmarkDashboard];
    }
    if (this.extraDashboards?.length > 0) {
      dashboards = [
        ...dashboards,
        ...this.extraDashboards.map((dashboard) => pick(dashboard, [`id`, `title`, `creator_name`])),
      ];
    }
    return uniqBy(dashboards, (d) => d.id);
  }

  showAvailableDashboardsDropdown() {
    // this is set whenever you click on an existing report from a dashboard or create a new report from the modal, so
    // no matter what we want to show the dropdown
    if (this.helpers.getBoardsMainDashboard()) {
      return true;
    }
    // this is set whenever you click on an existing report from a dashboard
    // if we get to this point, that means that we didn't come from the dashboard, and so in the beginning the dropdown
    // will be empty until we load all the dashboards
    return this.hasSelectedEntity && this.getAllDashboards().length > 0;
  }

  getUndoRedoActions(): SectionItem[] {
    const {canUseUndoRedo, canApplyUndo, canApplyRedo} = this.getContext(`historyManager`);
    if (!canUseUndoRedo) {
      return [];
    }

    return [
      {
        label: `Undo`,
        icon: `undo`,
        isDisabled: canApplyUndo === false,
        sideLabel: generateShortcutLabel(`Z`, {modifiers: [getCommandKey()], joinStr: ` `}),
        value: HeaderAction.Undo,
        elref: `reportUndoButton`,
      },
      ...(canApplyRedo
        ? [
            {
              label: `Redo`,
              icon: `redo`,
              sideLabel: generateShortcutLabel(`Z`, {modifiers: [KeyModifiers.Shift, getCommandKey()], joinStr: ` `}),
              value: HeaderAction.Redo,
              elref: `reportRedoButton`,
            },
          ]
        : []),
    ];
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  getHeaderActionItems(): Section {
    const saveTooltipText = this.getSaveTooltipText();
    const createdBy = {
      slot: `createdBy`,
      label: null,
      value: HeaderAction.CreatedBy,
    };

    const addFilter = {
      icon: `filter`,
      label: this.params.toggleGlobalFiltersLabel,
      value: HeaderAction.ToggleGlobalFilters,
    };

    const actions: SectionItem[] = [];
    const querySamplingAction: SectionItem[] = [];

    if (this.isReportModeEnabled()) {
      // disabled states set in slot declaration
      actions.push(createdBy, this.getShareButtonAction(`Copy URL`));
      if (this.params.isQuerySamplingActionVisible) {
        querySamplingAction.push(this.getQuerySamplingHeaderItem());
      }
    } else if (this.isDashboardModeEnabled()) {
      // When `is-select` mode is on for the share button, remove tooltip
      // section to avoid double tooltip for the share button and the
      // menu item in mp-select
      actions.push(
        addFilter,
        createdBy,
        omit(this.getShareButtonAction(), `tooltip`),
        ...(this.params.canWrite ? [RESET_ACTION] : []),
      );
    } else {
      // When `is-select` mode is on for the share button, remove tooltip
      // section to avoid double tooltip for the share button and the
      // menu item in mp-select
      actions.push(
        createdBy,
        omit(this.getShareButtonAction(), `tooltip`),
        ...(this.params.canWrite
          ? [
              RESET_ACTION,
              this.helpers.getSaveAction(),
              {
                icon: `duplicate`,
                label: `Save as New`,
                tooltip: this.params.invalidEntity ? {text: saveTooltipText} : null,
                value: HeaderAction.SaveNew,
                elref: `reportSaveNewButton`,
                isDisabled: this.params.invalidEntity,
              },
            ]
          : []),
        // In dashboards HeaderAction.Load and HeaderAction.Reset are hidden, and `alerts-button` is not given
        {
          value: HeaderAction.Load,
          label: `Open`,
          icon: `open`,
          elref: `reportLoadButton`,
        },
        {
          slot: `alerts-button`,
          label: ``,
        },
      );
    }

    // QTS should be the last button before the overflow menu, so put it at the end of the items array
    return {
      items: [
        ...this.filterHiddenActions(actions),
        ...this.getControlItems(),
        ...this.filterHiddenActions(querySamplingAction),
      ],
    };
  }

  private getShareButtonAction(shareTooltip = null) {
    if (!this.helpers.isShareButtonEnabled()) {
      if (this.params.invalidEntity) {
        shareTooltip = this.params.invalidMessage || `Current report is invalid`;
      } else {
        const shareCapability = this.helpers.getShareCapability();
        shareTooltip = shareCapability.disabledShareReason;
      }
    }
    const shareAction = {
      slot: `sharing`,
      tooltip: {text: shareTooltip || `Manage Sharing`},
      label: null,
      value: HeaderAction.Share,
    };

    return shareAction;
  }

  private track(event: string, props: Obj = {}) {
    if (this.params.trackName) {
      emitTrackEvent(this, {
        eventName: event,
        eventPrefix: `mp-query-report-header`,
        props: {
          ...props,
          project_id: this.params.projectId,
          tab: this.params.trackName,
          workspace_id: this.params.workspaceId,
        },
        cancelable: true,
      });
    }
  }

  private setSuperProperties(properties: Obj = {}) {
    mixpanel.register(properties);
  }

  private filterHiddenActions(actions: Array<SectionItem<HeaderAction>>): Array<SectionItem<HeaderAction>> {
    const hiddenActions = new Set(this.params.hideActions);
    return actions.filter((action) => !hiddenActions.has(action.value));
  }

  isReportModeEnabled() {
    const allowedReports = getDashboardAllowedReports();
    const report = getReportNameFromSearchContext(this.params.displayContext);
    return allowedReports.includes(report);
  }

  isDashboardModeEnabled() {
    return this.params.displayContext === SearchDisplayContext.Dashboard;
  }

  getSaveTooltipText() {
    let saveTooltip = ``;
    if (this.isSaveButtonDisabled()) {
      const entityTypeName = getEntityTypeName(this.params.entityType);
      if (this.params.invalidEntity) {
        saveTooltip = this.params.invalidMessage || `Current ${entityTypeName} is invalid`;
      } else if (this.hasSelectedEntity && !this.canSave()) {
        saveTooltip = this.helpers.isSharingEnabled()
          ? `You have view-only access and cannot save changes. [Duplicate to create a copy](${this.params.duplicateReportUrl}).`
          : `Editing is restricted on this report. Duplicate to create a copy.`;
      } else if (!this.params.canWrite || !this.state.canSaveToMainDashboard) {
        saveTooltip = `You have view-only access and cannot save changes`;
      } else if (!this.hasUnsavedChanges) {
        saveTooltip = `No unsaved changes`;
      }
    }
    return saveTooltip;
  }

  // the 'originDashboard' param usually comes from the URL, so we decode the dashboard title
  private getDecodedOriginDashboard() {
    const originDashboard = this.params.originDashboard;
    if (originDashboard) {
      return {
        ...originDashboard,
        title: decodeURIComponent(originDashboard.title),
      };
    }
    return null;
  }

  private getQuerySamplingHeaderItem(): SectionItem {
    const disabledMessage = this.params.querySamplingDisabledMessage;
    const isQuerySamplingEnabled = this.params.isQuerySamplingEnabled;
    const isTourTriggered = this.params.triggerQuerySamplingTourTooltip;
    const tooltipText =
      disabledMessage || (isQuerySamplingEnabled ? DISABLE_QUERY_SAMPLING : ENABLE_QUERY_SAMPLING_LONG);

    const querySamplingAction = {
      slot: `toggle-query-sampling`,
      tooltip: tooltipText && !isTourTriggered ? {text: tooltipText, placement: Placement.Top} : null,
      label: null,
      value: HeaderAction.QuerySampling,
    };
    return querySamplingAction;
  }

  private getQuerySamplingOverflowItems(): SectionItem[] {
    const disabledMessage = this.params.querySamplingDisabledMessage;
    const isQuerySamplingEnabled = this.params.isQuerySamplingEnabled;
    return [
      {
        label: isQuerySamplingEnabled ? DISABLE_QUERY_SAMPLING : ENABLE_QUERY_SAMPLING,
        icon: isQuerySamplingEnabled ? `filled-sampling` : `sampling`,
        highlightIcon: isQuerySamplingEnabled,
        isDisabled: !!disabledMessage,
        tooltip: disabledMessage ? {text: disabledMessage, placement: Placement.Left} : null,
        value: HeaderAction.QuerySampling,
      },
    ];
  }

  private getAlertItems() {
    if (this.helpers.isAlertFeatureGateEnabled()) {
      const tooltip: TooltipInfo = this.helpers.isAlertButtonDisabled()
        ? {text: this.helpers.getAlertsTooltipText(), placement: Placement.Left}
        : null;
      return [
        {
          label: `Alerts`,
          icon: `alert-normal`,
          isDisabled: this.helpers.isAlertButtonDisabled(),
          tooltip,
          subSections: [
            {
              items: [
                {
                  label: `Create Alert`,
                  value: HeaderAction.CreateAlert,
                  icon: `plus`,
                  isDisabled: this.helpers.isAlertButtonDisabled(),
                  tooltip,
                },
                {
                  label: `Manage Alerts`,
                  value: HeaderAction.ManageAlert,
                  icon: `settings`,
                  isDisabled: this.helpers.isAlertButtonDisabled(),
                  tooltip,
                },
              ],
            },
          ],
        },
      ];
    } else {
      const tooltip: TooltipInfo = {text: `Enable Alerts`, placement: Placement.Left};
      return [
        {
          label: `Alerts`,
          icon: `alert-normal`,
          tooltip,
          value: HeaderAction.EnableAlerts,
        },
      ];
    }
  }

  private resetPresenceMonitor(resourceId: number, entityType: EntityType.Report | EntityType.Dashboard) {
    this.presenceActivityMonitor?.stop();
    const projectId = this.params.projectId;
    if (currentProjectStore.value?.isDemo || isEmbedMode()) {
      return;
    }

    if (resourceId && projectId && entityType) {
      const callback = (users) => this.updatePresenceIcons(users);
      this.presenceActivityMonitor = getPresenceActivityMonitor({projectId, entityType, resourceId, callback}).start();
    }
  }

  private updatePresenceIcons({views, total}: PresenceResults) {
    const presenceViews: MixpanelUserItem[] = views.map(({user, timestamp}) => ({
      id: user.id,
      email: user.email,
      name: user.name,
      lastViewedUtcSeconds: timestamp,
    }));

    this.update({presenceViews, totalPresenceViews: total});
  }

  getPresenceTrackingProps() {
    const nowInSeconds = new Date().getTime() / 1000;
    const oneMonthAgoUtcSeconds = nowInSeconds - 30 * 24 * 60 * 60;

    // We exclude the creator
    const numberOfViews = this.state.presenceViews?.slice(1).length || 0;
    const numberOfRecentViews =
      this.state.presenceViews?.slice(1).filter((view) => view.lastViewedUtcSeconds >= oneMonthAgoUtcSeconds).length ||
      0;

    return {
      [TrackingEventProperty.Source]: `report-header`,
      [TrackingEventProperty.ViewerType]: this.isOwnerUser() ? `Creator` : `Viewer`,
      [TrackingEventProperty.NumberOfViews]: numberOfViews,
      [TrackingEventProperty.NumberOfRecentViewsLast30d]: numberOfRecentViews,
      [TrackingEventProperty.ResourceType]: this.params.entityType,
      [TrackingEventProperty.ResourceSubType]: this.params.displayContext,
    };
  }

  private tryToShowCreateAlertDialog() {
    // raf to make sure conditions behind alert button have settled
    requestAnimationFrame(() => {
      if (!this.helpers.isAlertButtonDisabled()) {
        this.update({isCreateAlertModalOpen: true});
      } else {
        addToast({
          message: this.helpers.getAlertsTooltipText(),
          type: ToastType.WARNING,
        });
      }
    });
  }
}

export const QueryReportHeaderEl = defineAndMakeJsxFactory<JsxProps>(`mp-query-report-header`, QueryReportHeader);
export const QueryReportHeaderReact = panel2react<JsxProps>(`mp-query-report-header`, QueryReportHeader);
